import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { Input } from '@wds/input';
import { Button } from '@wds/button';
import { Slide } from '@wds/motion';
import { ProgressCircle } from '@wds/progress-circle';

import { getCookie } from 'sma.common/utils';
import STRINGS from '../constants';
import MFAErrorMessage from './MFAErrorMessage';
import { clearCooldownTimer, startCooldownTimer } from '../utils/cooldown';

const RESEND_COOLDOWN_SEC = 120;

const RecoveryEmailSetup = (props) => {
    const {
        codes,
        error: initialError,
        finishBtnTxt,
        handleOnFinish,
        handleError,
        showFinishButton,
        isLoaded,
        currentPage,
        isSliding,
        setIsSliding,
        initialPendingEmail,
        initialCooldown,
        resetSignal,
        onSetupStateChange,
        onRecoveryEmailVerified,
        isModal,
    } = props;
    const [copiedSuccessfully, setCopiedSuccessfully] = useState(false);
    const timerRef = useRef(null);
    const cooldownEndAtRef = useRef(0);

    const hasPendingEmail = Boolean(initialPendingEmail);
    const [step, setStep] = useState(hasPendingEmail ? 'verify-code' : 'enter-email');
    const [email, setEmail] = useState(initialPendingEmail || '');
    const [code, setCode] = useState('');
    const [error, setError] = useState(initialError || '');
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [isVerified, setIsVerified] = useState(false);
    const [codeAlreadyPending, setCodeAlreadyPending] = useState(hasPendingEmail && initialCooldown > 0);

    // Track when recovery email setup is initiated
    useEffect(() => {
        if (currentPage === 'recovery-email-setup' && window.track) {
            window.track('mfaRecoveryEmailSetupStarted', {
                setupContext: 'enrollment',
                hasPendingEmail: hasPendingEmail
            });
        }
    }, [currentPage]);

    useEffect(() => {
        if (hasPendingEmail && initialCooldown > 0) {
            startCooldown(initialCooldown);
        }
        return () => {
            clearCooldownTimer(timerRef);
        };
    }, []);

    useEffect(() => {
        if (!initialPendingEmail) {
            clearCooldownTimer(timerRef);
            cooldownEndAtRef.current = 0;
            setCooldown(0);
            setCode('');
            setCodeAlreadyPending(false);
            setStep('enter-email');
            setEmail('');
        }
    }, [initialPendingEmail]);

    useEffect(() => {
        if (!resetSignal) return;
        clearCooldownTimer(timerRef);
        cooldownEndAtRef.current = 0;
        setCooldown(0);
        setCode('');
        setCodeAlreadyPending(false);
        setStep('enter-email');
        setEmail('');
        setError('');
    }, [resetSignal]);

    useEffect(() => {
        if (onSetupStateChange) {
            onSetupStateChange(step);
        }
    }, [step, onSetupStateChange]);

    const isValidEmail = (emailStr) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(emailStr);
    };

    const isValidCode = code.length === 6 && /^[0-9]{6}$/.test(code);

    const startCooldown = (remaining) => {
        startCooldownTimer({
            remaining,
            defaultSeconds: RESEND_COOLDOWN_SEC,
            setCooldown,
            timerRef,
            endAtRef: cooldownEndAtRef,
        });
    };

    const handleCodeChange = (e) => {
        // Only allow digits, max 6 characters
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setCode(value);
    };

    /**
     * Send verification code to the recovery email address.
     * @param {boolean} isResend - Whether this is a resend request
     */
    const sendVerificationCode = async (isResend = false) => {
        if (isResend && (cooldown > 0 || resending)) return;

        if (!isResend && !isValidEmail(email)) {
            setError(STRINGS.invalidEmailFormat || 'Please enter a valid email address');
            return;
        }

        if (isResend) {
            setResending(true);
        } else {
            setLoading(true);
        }
        setError('');
        if (isResend) setCode('');

        try {
            const response = await fetch(reverse('api-internal-mfa-recovery-email'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken'),
                },
                body: JSON.stringify({
                    action: 'setup_recovery_email',
                    email: email,
                }),
            });

            const data = await response.json();

            if (data.success) {
                if (!isResend) {
                    setStep('verify-code');
                }
                setCodeAlreadyPending(false);
                startCooldown();
                // Track code sent
                if (window.track) {
                    window.track('mfaRecoveryEmailCodeSent', {
                        setupContext: 'enrollment',
                        isResend: isResend,
                        email: email
                    });
                }
            } else if (data.error === 'code_already_pending' && data.time_remaining) {
                setStep('verify-code');
                setCodeAlreadyPending(true);
                startCooldown(data.time_remaining);
                setError('code_already_pending');
            } else if (data.error === 'cooldown_active' && data.time_remaining) {
                setCodeAlreadyPending(true);
                startCooldown(data.time_remaining);
                if (!isResend) {
                    setStep('verify-code');
                }
                setError('code_already_pending');
            } else {
                setError(STRINGS?.[data.error] || data.error || STRINGS.unknownError);
            }
        } catch (err) {
            setError(STRINGS.unknownError);
            handleError?.(err);
        } finally {
            if (isResend) {
                setResending(false);
            } else {
                setLoading(false);
            }
        }
    };

    const handleSendCode = () => sendVerificationCode(false);
    const handleResendCode = () => sendVerificationCode(true);

    const handleVerifyCode = async () => {
        if (!isValidCode) {
            setError(STRINGS.invalidCode || 'Please enter a valid 6-digit code');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(reverse('api-internal-mfa-recovery-email'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken'),
                },
                body: JSON.stringify({
                    action: 'verify_recovery_email',
                    code: code,
                }),
            });

            const data = await response.json();

            if (data.success) {
                // Track successful verification
                if (window.track) {
                    window.track('mfaRecoveryEmailVerified', {
                        setupContext: 'enrollment',
                        email: email
                    });
                }
                // Show verifying screen, then complete enrollment
                setStep('verifying');
                await completeEnrollment();
            } else {
                // Check for lockout - redirect immediately without showing verifying screen
                if (data.error === 'invalid_code_locked_out') {
                    // Track failure before redirect
                    if (window.track) {
                        window.track('mfaRecoveryEmailSetupFailed', {
                            setupContext: 'enrollment',
                            errorType: 'locked_out'
                        });
                    }
                    window.location.href = reverse('mfa-lockout');
                    return;
                }
                // Track other verification failures
                if (window.track) {
                    window.track('mfaRecoveryEmailSetupFailed', {
                        setupContext: 'enrollment',
                        errorType: data.error || 'invalid_code'
                    });
                }
                setError(STRINGS?.[data.error] || data.error || STRINGS.invalidCode);
                setCode('');
            }
        } catch (err) {
            setError(STRINGS.unknownError);
            handleError?.(err);
        } finally {
            setLoading(false);
        }
    };

    const completeEnrollment = async () => {
        try {
            const response = await fetch(reverse('api-internal-mfa-recovery-email'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken'),
                },
                body: JSON.stringify({
                    action: 'complete_enrollment',
                }),
            });

            const data = await response.json();

            if (data.success) {
                if (isModal) {
                    if (onRecoveryEmailVerified) {
                        onRecoveryEmailVerified(email);
                    }
                    handleOnFinish();
                } else {
                    setIsVerified(true);
                    setStep('success');
                }
                // Track successful completion
                if (window.track) {
                    window.track('mfaRecoveryEmailSetupCompleted', {
                        setupContext: 'enrollment',
                        email: email
                    });
                }
                // Show success screen
                setIsVerified(true);
                setStep('success');
            } else {
                setError(STRINGS?.[data.error] || STRINGS.unknownError);
                setStep('verify-code');
            }
        } catch (err) {
            setError(STRINGS.unknownError);
            setStep('verify-code');
        }
    };

    const handleContinueToSite = () => {
        if (isVerified && email && onRecoveryEmailVerified) {
            onRecoveryEmailVerified(email);
        }
        handleOnFinish();
    };

    return (
        <Slide
            in={isLoaded && currentPage === 'recovery-email-setup'}
            speed={currentPage === 'recovery-email-setup' ? 'slow' : undefined}
            timeout={currentPage === 'recovery-email-setup' ? undefined : 150}
            direction="right"
            onEnter={() => setIsSliding(true)}
            onExited={() => setIsSliding(false)}
            alwaysRenderContent
        >
            <Box
                display="flex"
                flexDirection="column"
                gap={4}
                style={{ position: isSliding ? 'absolute' : 'static', width: '100%' }}
                mb={6}
            >
                <MFAErrorMessage error={error} />
                {step === 'enter-email' && (
                    <Box display="flex" flexDirection="column" gap={4}>
                        {/* You are all set section */}
                        <Box>
                            <Typography variant="sectionTitle" weight="medium">
                                {STRINGS.allSet || "You're all set!"}
                            </Typography>
                            <Typography component="p" variant="bodySm" style={{ marginTop: '8px' }}>
                                {STRINGS.whenYouLoginMFA || "When you log in, you'll be asked to verify your identity using multi-factor authentication."}
                                {' '}
                                <Typography
                                    component="a"
                                    variant="link"
                                    href={window.LOCALE?.mfaLearnMore || reverse('mfa-learn-more')}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {STRINGS.learnMoreAboutManagingMFA || 'Learn more about managing MFA.'}
                                </Typography>
                            </Typography>
                        </Box>

                        {/* Add recovery email section */}
                        <Box style={{ marginTop: '16px' }}>
                            <Typography variant="sectionTitleSm" weight="medium">
                                {STRINGS.addRecoveryEmailTitle || 'Add recovery email address'}
                            </Typography>
                            <Typography component="p" variant="bodySm" color="secondary" style={{ marginTop: '8px' }}>
                                {STRINGS.recoveryEmailBackupDescription ||
                                 'Get a backup option to sign in to help keep your account secure in the event that you are unable to access your device.'}
                            </Typography>
                        </Box>

                        <Input
                            value={email}
                            placeholder={STRINGS.addRecoveryEmailPlaceholder || 'Add recovery email address'}
                            onChange={(e) => setEmail(e.target.value)}
                            id="recovery-email-input"
                            type="email"
                            stretched
                            aria-required="true"
                            autoFocus
                        />

                        <Button
                            id="verify-email-button"
                            size="lg"
                            disabled={!isValidEmail(email) || loading}
                            onClick={handleSendCode}
                        >
                            {!loading ? (
                                STRINGS.verify || 'Verify'
                            ) : (
                                <ProgressCircle size='sm' continuous />
                            )}
                        </Button>
                    </Box>
                )}

                {step === 'verify-code' && (
                    <Box display="flex" flexDirection="column" gap={4}>
                        {codeAlreadyPending && error !== 'code_already_pending' && (
                            <MFAErrorMessage error="code_already_pending" />
                        )}
                        {/* Verification code header */}
                        <Box>
                            <Typography variant="sectionTitle" weight="medium">
                                {STRINGS.verificationCodeTitle || 'Verification code'}
                            </Typography>
                            <Typography component="p" variant="bodySm" color="secondary" style={{ marginTop: '8px' }}>
                                {STRINGS.enterOtpDescription ||
                                 'Enter one-time verification code sent to your recovery email address'}
                                {email && (
                                    <>
                                        {' ('}
                                        <span style={{ fontWeight: '700' }}>{email}</span>
                                        {')'}
                                    </>
                                )}
                            </Typography>
                        </Box>

                        {/* Verification code input */}
                        <Input
                            value={code}
                            placeholder={STRINGS.enterCodeProvided || 'Enter the code provided'}
                            onChange={handleCodeChange}
                            id="verification-code-input"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            stretched
                            aria-required="true"
                            autoFocus
                        />

                        {/* Resend Button */}
                        <Box style={{ textAlign: 'right' }}>
                            <Button
                                onClick={handleResendCode}
                                variant='text'
                                disabled={cooldown > 0 || resending}
                            >
                                <span style={{ color: '#007FAA' }}>
                                    {resending
                                        ? STRINGS.sending
                                        : cooldown > 0
                                        ? `${STRINGS.resend} (${cooldown}s)`
                                        : STRINGS.resend}
                                </span>
                            </Button>
                        </Box>

                        {/* Confirm button */}
                        <Button
                            id="confirm-code-button"
                            size="lg"
                            disabled={!isValidCode || loading}
                            onClick={handleVerifyCode}
                        >
                            {!loading ? (
                                STRINGS.confirm || 'Confirm'
                            ) : (
                                <ProgressCircle size='sm' continuous />
                            )}
                        </Button>
                    </Box>
                )}

                {(step === 'verifying' || step === 'success') && (
                    <Box display="flex" flexDirection="column" gap={4}>
                        {/* Verification code header */}
                        <Box>
                            <Typography variant="sectionTitle" weight="medium">
                                {STRINGS.verificationCodeTitle || 'Verification code'}
                            </Typography>
                            {!isVerified && (
                                <Typography component="p" variant="bodySm" color="secondary" style={{ marginTop: '8px' }}>
                                    {STRINGS.pleaseWait || 'Please wait...'}
                                </Typography>
                            )}
                        </Box>

                        {/* Green circle indicator */}
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            style={{ marginTop: '32px', marginBottom: '16px' }}
                        >
                            {/* Green circle */}
                            <Box
                                style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '50%',
                                    border: '4px solid #00AA55',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {isVerified && (
                                    <svg
                                        width="32"
                                        height="32"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#00AA55"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                )}
                            </Box>

                            {/* Success message */}
                            {isVerified && (
                                <Typography
                                    component="p"
                                    variant="bodySm"
                                    style={{ marginTop: '16px', color: '#00AA55' }}
                                >
                                    {STRINGS.accountSuccessfullyVerified || 'Account successfully verified!'}
                                </Typography>
                            )}
                        </Box>

                        {/* Continue to site button */}
                        {isVerified && (
                            <Button
                                id="continue-to-site-button"
                                size="lg"
                                onClick={handleContinueToSite}
                            >
                                {STRINGS.continueToSite || 'Continue to site'}
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
        </Slide>
    );
};

RecoveryEmailSetup.propTypes = {
    isLoaded: PropTypes.bool.isRequired,
    currentPage: PropTypes.string.isRequired,
    isSliding: PropTypes.bool.isRequired,
    setIsSliding: PropTypes.func.isRequired,
    handleOnFinish: PropTypes.func.isRequired,
    handleError: PropTypes.func,
    initialPendingEmail: PropTypes.string,
    initialCooldown: PropTypes.number,
    resetSignal: PropTypes.number,
    onSetupStateChange: PropTypes.func,
    onRecoveryEmailVerified: PropTypes.func,
    isModal: PropTypes.bool,
};

RecoveryEmailSetup.defaultProps = {
    handleError: () => {},
    initialPendingEmail: '',
    initialCooldown: 0,
    resetSignal: 0,
    onSetupStateChange: null,
    onRecoveryEmailVerified: null,
    isModal: false,
};

export default RecoveryEmailSetup;
